import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  Text,
  Textarea,
  useOutsideClick,
} from '@chakra-ui/react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Scenario } from 'models/response/scenario.interface';
import { radius } from 'styles/revampTokens';
import ScenarioDatePicker from 'pages/PlanRevamp/Plan/Filters/ScenarioDatePicker';
import { CreateScenarios, FetchScenarios } from 'redux/BuilderModeSlice';
import {
  FetchPlannerModeItems,
  RenameScenario,
  getPlannerBlock,
  getPlannerModel,
  getBaseScenario,
  FetchBlockOutputs,
} from 'redux/PlannerModeSlice';

const validationSchema = Yup.object({
  scenarioName: Yup.string().required('Scenario name is required'),
  scenarioDescription: Yup.string(),
  // startDate: Yup.string().required('StartDate is required'),
});

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const ScenarioFormForecast = ({
  scenarios,
  baseScenarios,
  createScenario,
  getAllscenarios,
  editScenarioData,
  isEdit,
  renameScenarioApi,
  setIsEdit,
  onCancel,
}: any) => {
  const { modelId, blockId } = useParams();
  const [startIn, setStartIn] = useState<string>('');
  const blockData = useSelector(getPlannerModel);
  const baseScenario = useSelector(getBaseScenario);
  const dispatch: any = useDispatch();
  const plannerBlock: any = useSelector(getPlannerBlock);
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;

  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(baseScenarios?.id);
  const selectedScenario = scenarios.find(
    (scenario: Scenario) => scenario?.id === selectedScenarioId,
  );
  useEffect(() => {
    setSelectedScenarioId(baseScenarios?.id);
  }, []);
  useEffect(() => {
    if (isEdit) {
      setStartIn(editScenarioData?.start_date);
    } else {
      setStartIn(blockData?.time_properties?.starting_period);
    }
  }, [isEdit]);
  const fetchIndicatorsData = () => {
    const idArray = plannerBlock?.dimensions
      .filter((item: any) => item.name !== 'Time')
      .map((item: any) => item.id);
    const dimensionIds = idArray.join(', ');
    const payload = {
      blockId,
      params: {
        dim_id: dimensionIds,
        indicator_filter: 'all',
        scenario_id: baseScenario?.id,
      },
    };
    dispatch(FetchBlockOutputs(payload));
  };

  const handleSubmit = async (values: any, { resetForm }: any) => {
    if (isEdit) {
      let requestBody = {};
      if (values.scenarioName !== editScenarioData?.name) {
        requestBody = { ...requestBody, name: values?.scenarioName };
      }
      if (values.scenarioDescription !== editScenarioData?.description) {
        requestBody = { ...requestBody, description: values?.scenarioDescription };
      }
      if (values.startDate !== editScenarioData?.start_date) {
        requestBody = { ...requestBody, start_date: values?.startDate };
      }

      if (Object.keys(requestBody).length === 0) {
        onCancel();
        resetForm();
        return;
      }

      await renameScenarioApi({
        item_id: editScenarioData?.id,
        requestBody,
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: true,
        },
      });

      if (values.startDate !== editScenarioData?.start_date) {
        const dimId = plannerBlock?.dimensions?.find((data: any) => {
          return data?.name === 'Time';
        });
        const startDateObject = new Date(values.startDate);
        const yearFormat = startDateObject.getFullYear();
        const monthFormat = `${monthNames[startDateObject.getMonth()]}-${String(yearFormat).slice(
          -2,
        )}`;
        dispatch(
          FetchPlannerModeItems({
            dimensionId: dimId?.id,
            params: { scenario_id: baseScenarios?.id },
            actuals:
              blockData?.time_properties?.time_granularity.toLowerCase() === 'm'
                ? monthFormat
                : yearFormat,
          }),
        );
        fetchIndicatorsData();
      }
    } else {
      const crearteRequestBody = {
        modelId,
        data: {
          name: values?.scenarioName,
          description: values?.scenarioDescription,
          duplicate_id: selectedScenarioId,
          start_date: values?.startDate,
        },
        toaster: {
          successMessage: 'Successfully added scenario',
          errorMessage: 'A scenario with this name already exists',
        },
      };
      await createScenario(crearteRequestBody);
    }
    await getAllscenarios(modelId);
    resetForm();
    onCancel();
  };
  const handleCancel = (resetForm: () => void) => {
    setIsEdit(false);
    onCancel();
    resetForm();
  };

  const handleClearSelection = () => {
    setStartIn('');
  };

  useOutsideClick({
    ref: reference,
    handler: () => {
      setStartIn('');
      setIsEdit(false);
    },
  });

  return (
    <Box ref={reference} width='100%'>
      <Flex direction='column' align='center' gap='20px' width='100%'>
        <Text
          textAlign='center'
          fontFamily='Comfortaa Bold'
          fontWeight='700'
          fontSize='18px'
          color='#121826'
        >
          {isEdit ? 'Edit Scenario' : 'Add New Scenario'}
        </Text>
        <Formik
          initialValues={{
            scenarioName: editScenarioData?.name || '',
            scenarioDescription: editScenarioData?.description || '',
            duplicateScenarioId: '',
            startDate: editScenarioData?.start_date || '',
          }}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue, resetForm }: any) => (
            <Form style={{ width: '100%' }}>
              <Flex direction='column' gap='16px' width='100%'>
                <Flex direction='column' gap='4px' width='100%'>
                  <Text
                    fontFamily='Roboto Regular'
                    fontWeight='600'
                    fontSize='11px'
                    color='#708090'
                  >
                    Name
                  </Text>
                  <Box
                    as='input'
                    value={values?.scenarioName}
                    onChange={(event: any) => setFieldValue('scenarioName', event.target.value)}
                    placeholder='Enter scenario name here'
                    autoFocus
                    width='100%'
                    bg='white'
                    border={`1px solid ${
                      errors.scenarioName && touched.scenarioName ? '#E23F3F' : '#D1D6DD'
                    }`}
                    borderRadius={radius.control}
                    p='12px'
                    fontFamily='Roboto Medium, Roboto Regular'
                    fontSize='13px'
                    color='#121826'
                    outline='none'
                    _placeholder={{ color: '#BEBEBE' }}
                    _focus={{ borderColor: '#8B5CF6' }}
                  />
                  {errors.scenarioName && touched.scenarioName && (
                    <Text fontSize='11px' color='#E23F3F' fontWeight='500'>
                      {errors.scenarioName}
                    </Text>
                  )}
                </Flex>

                <Flex direction='column' gap='4px' width='100%'>
                  <Text
                    fontFamily='Roboto Regular'
                    fontWeight='600'
                    fontSize='11px'
                    color='#708090'
                  >
                    Description
                  </Text>
                  <Textarea
                    value={values?.scenarioDescription}
                    onChange={(event) => setFieldValue('scenarioDescription', event.target.value)}
                    placeholder='Write something about this Scenario...'
                    height='59px'
                    resize='none'
                    bg='white'
                    border='1px solid #D1D6DD'
                    borderRadius={radius.control}
                    p='12px'
                    fontFamily='Roboto Medium, Roboto Regular'
                    fontSize='13px'
                    color='#121826'
                    _placeholder={{ color: '#BEBEBE' }}
                    _focus={{ borderColor: '#8B5CF6', boxShadow: 'none' }}
                  />
                </Flex>

                {!isEdit && (
                  <Flex direction='column' gap='4px' width='100%'>
                    <Text
                      fontFamily='Roboto Regular'
                      fontWeight='600'
                      fontSize='11px'
                      color='#708090'
                    >
                      Duplicate Scenario
                    </Text>
                    <Menu>
                      <MenuButton
                        as={Button}
                        bg='white'
                        border='1px solid #D1D6DD'
                        borderRadius={radius.control}
                        px='12px'
                        height='43px'
                        width='100%'
                        fontWeight='400'
                        fontFamily='Roboto Medium, Roboto Regular'
                        fontSize='13px'
                        color='#121826'
                        textAlign='left'
                        rightIcon={<ChevronDownIcon color='#708090' />}
                        _hover={{ borderColor: '#8B5CF6' }}
                        _active={{ bg: 'white' }}
                      >
                        <Box
                          overflow='hidden'
                          whiteSpace='nowrap'
                          textOverflow='ellipsis'
                          maxWidth='calc(100% - 20px)'
                        >
                          {selectedScenario?.name || 'Select Scenario'}
                        </Box>
                      </MenuButton>
                      <MenuList
                        borderRadius={radius.control}
                        bg='white'
                        border='1px solid #D1D6DD'
                        boxShadow='0px 4px 24px rgba(0,0,0,0.15)'
                        maxHeight='200px'
                        overflowY='auto'
                        zIndex={6000}
                      >
                        {scenarios?.map((scenario: Scenario) => (
                          <MenuItemOption
                            key={scenario?.id}
                            className='scenario-menu'
                            bg={selectedScenario?.id === scenario?.id ? '#EFEEFE' : 'transparent'}
                            width='100%'
                            value={scenario.name}
                            onClick={() => {
                              setFieldValue('duplicateScenarioId', scenario?.id);
                              setSelectedScenarioId(scenario?.id);
                            }}
                            _hover={{ bg: '#F5F5F5' }}
                          >
                            <Text
                              overflow='hidden'
                              whiteSpace='nowrap'
                              textOverflow='ellipsis'
                              fontSize='13px'
                              fontFamily='Roboto Regular'
                              color='#121826'
                            >
                              {scenario.name}
                            </Text>
                          </MenuItemOption>
                        ))}
                      </MenuList>
                    </Menu>
                  </Flex>
                )}

                <Flex direction='column' gap='4px' width='100%'>
                  <Text
                    fontFamily='Roboto Regular'
                    fontWeight='600'
                    fontSize='11px'
                    color='#708090'
                  >
                    Forecast start date
                  </Text>
                  <Box width='100%'>
                    <ScenarioDatePicker
                      clearSection={false}
                      clearDateInput={handleClearSelection}
                      selected={startIn ? new Date(startIn) : null}
                      onChange={(date: Date) => {
                        setStartIn(date.toDateString());
                        setFieldValue('startDate', date.toDateString());
                      }}
                      timescale={blockData?.time_properties?.time_granularity === 'Y' ? 'Y' : 'M'}
                    />
                  </Box>
                  <ErrorMessage name='startDate' component='div' className='error' />
                </Flex>

                <Flex gap='16px' width='100%' height='43px' mt='4px'>
                  <Button
                    flex={1}
                    bg='white'
                    border='1px solid #D1D6DD'
                    borderRadius={radius.control}
                    fontFamily='Roboto Medium, Roboto Regular'
                    fontWeight='500'
                    fontSize='13px'
                    color='#121826'
                    height='100%'
                    _hover={{ bg: 'gray.50' }}
                    _active={{ transform: 'scale(0.97)' }}
                    onClick={() => handleCancel(resetForm)}
                  >
                    Cancel
                  </Button>
                  <Button
                    flex={1}
                    bg='#8B5CF6'
                    borderRadius={radius.control}
                    fontFamily='Roboto Medium, Roboto Regular'
                    fontWeight='500'
                    fontSize='13px'
                    color='white'
                    height='100%'
                    _hover={{ bg: '#7C4EE0' }}
                    _active={{ transform: 'scale(0.97)' }}
                    type='submit'
                  >
                    {isEdit ? 'Save Changes' : 'Create Scenario'}
                  </Button>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
    </Box>
  );
};

const mapStateToProperties = (state: any) => {
  return {
    scenarios: state.builderModeSlice?.allScenarios,
    baseScenarios: state.builderModeSlice?.baseScenario,
  };
};
const mapDispatchToProperties = (dispatch: any) => {
  return {
    createScenario: (requestBody: any) => dispatch(CreateScenarios(requestBody)),
    renameScenarioApi: (data: any) => dispatch(RenameScenario(data)),
    getAllscenarios: (modelId: number) => dispatch(FetchScenarios(modelId)),
  };
};

export default connect(mapStateToProperties, mapDispatchToProperties)(ScenarioFormForecast);
